import { useState, useCallback, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import type { FileNotesResponse } from '../types/api.types';
import { toast } from 'react-hot-toast';
import { useTour } from '@reactour/tour';
import { useFileActions } from '@/hooks/useFileActions';
import { useParams } from 'react-router-dom';

export const useNotes = (notesData?: FileNotesResponse) => {
  const [comment, setComment] = useState<string>(notesData?.comment || '');
  const [fraud, setFraud] = useState<boolean>(notesData?.fraud || false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isOpen, currentStep, setCurrentStep } = useTour();
  const { addNotes } = useFileActions();
  const { id } = useParams();

  // Synchronisation avec les données reçues
  useEffect(() => {
    if (notesData) {
      setComment(notesData.comment);
      setFraud(notesData.fraud);
    }
  }, [notesData]);

  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const handleFraudChange = useCallback(
    async (newValue: boolean) => {
      setFraud(newValue);
      setIsLoading(true);
      try {
        if (isOpen) {
          // Simuler un délai d'enregistrement pendant le tour
          await new Promise((resolve) => setTimeout(resolve, 500));
          toast.success('Vos notes ont été enregistrées');
        } else if (id) {
          // Comportement normal en dehors du tour
          await addNotes(id, comment, newValue);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isOpen, id, comment, addNotes]
  );

  const saveNotes = useCallback(
    async (fileId: string) => {
      setIsLoading(true);
      try {
        if (isOpen) {
          // Simuler un délai d'enregistrement pendant le tour
          await new Promise((resolve) => setTimeout(resolve, 500));
          toast.success('Vos notes ont été enregistrées');
          setCurrentStep(currentStep + 1);
        } else {
          // Comportement normal en dehors du tour
          await addNotes(fileId, comment, fraud);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isOpen, setCurrentStep, currentStep, addNotes, comment, fraud]
  );

  return {
    comment,
    fraud,
    isLoading,
    handleCommentChange,
    handleFraudChange,
    saveNotes,
  };
};
